import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  useReveal, IndustryHero, IndustryIntro, UseCasesGrid,
  IndustryStats, IndustryDeliverables, IndustryCTA, Footer,
} from '../shared/IndustryPageSections';

const COLOR = '#5D4037';

const USE_CASES = [
  { icon:'gauge', title:'Smart Factory & Machine Monitoring', to:'/industries/industrial-iot/smart-factory-machine-monitoring', desc:'Live visibility into machine uptime, cycle time, and shop-floor performance across every line.' },
  { icon:'tool', title:'Predictive Maintenance Solutions', to:'/industries/industrial-iot/predictive-maintenance-solutions', desc:'Vibration, temperature, and current sensing that predicts failures before they cause downtime.' },
  { icon:'dashboard', title:'Industrial Data Acquisition & Dashboards', to:'/industries/industrial-iot/industrial-data-acquisition-dashboards', desc:'Unified data collection from PLCs and sensors, visualised in real-time operational dashboards.' },
  { icon:'plug', title:'Connected Industrial IoT Platforms', to:'/industries/industrial-iot/connected-industrial-iot-platforms', desc:'Cloud-connected platforms that bring every machine and sensor onto one integrated system.' },
  { icon:'activity', title:'Industrial Asset & Utility Monitoring', to:'/industries/industrial-iot/industrial-asset-utility-monitoring', desc:'Track the health, location, and utilisation of industrial assets and utility systems.' },
  { icon:'automation', title:'Industrial Automation & Control Systems', to:'/industries/industrial-iot/industrial-automation-control-systems', desc:'IoT-triggered automation for material handling, process control, and quality inspection.' },
];

const STATS = [
  { val:'45%', label:'Average reduction in unplanned downtime' },
  { val:'20%', label:'Improvement in OEE within 6 months' },
  { val:'4 mo', label:'Average time to ROI for IIoT deployment' },
];

const DELIVERABLES = [
  'Retrofit IoT sensor nodes for existing machines',
  'Edge computing gateway for local data processing',
  'Plant-wide OEE and production dashboard',
  'Predictive maintenance alerts and reports',
  'Integration with existing SCADA and ERP systems',
  'Mobile app for maintenance teams and plant managers',
  'Environmental monitoring with regulatory threshold alerts',
  'Executive KPI dashboard with shift reports and trend analysis',
];

const INTRO_BODY = [
  "Manufacturing plants in India are losing lakhs every month to unplanned downtime, inefficient processes, and decisions made without data. Industrial IoT addresses all three: sensor intelligence on every machine, edge computing for local processing, and dashboards that give plant managers real-time visibility across every line on the floor.",
  'Zone-X builds IIoT systems that retrofit onto existing machinery without OEM dependency. Our systems collect machine health data, compute OEE metrics, and surface the insights that matter to plant managers, maintenance engineers, and production directors — all in real time, from anywhere, with an average ROI achieved in under 4 months.',
];

export default function IndustrialIoTPage() {
  const [heroRef, heroVisible] = useReveal();
  return (
    <>
      <Helmet>
        <title>Industrial IoT Solutions India | Predictive Maintenance & OEE Monitoring — Zone-X</title>
        <meta name="description" content="Zone-X builds industrial IoT systems in India — predictive maintenance, OEE monitoring, machine condition sensing, SCADA integration, and plant-wide data dashboards for manufacturers." />
        <link rel="canonical" href="https://zone-x.dev/industries/industrial-iot" />
      </Helmet>

      <div ref={heroRef}>
        <IndustryHero
          tag="Industry / Industrial IoT"
          breadcrumb="Industrial IoT"
          title="Connect Your Factory. Automate the Repetitive."
          titleAccent="Predict the Unexpected."
          subtitle="Zone-X delivers Industrial IoT (IIoT) and automation solutions that reduce downtime, improve OEE, and put your plant data to work."
          color={COLOR}
          visible={heroVisible}
          visual="/visuals/industries/Industry 5.png"
        />
      </div>

      <IndustryIntro heading="Industrial IoT — Built for the Shop Floor, Not Just the Boardroom" body={INTRO_BODY} color={COLOR} />
      <UseCasesGrid cases={USE_CASES} color={COLOR} />
      <IndustryStats stats={STATS} color={COLOR} />
      <IndustryDeliverables items={DELIVERABLES} color={COLOR} />
      <IndustryCTA />
      <Footer />
    </>
  );
}
